import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormErrorMessages } from '@core/constants/form-error-messages';
import { PasswordPattern } from '@core/constants/patterns';
import { AuthRoutes } from '@core/constants/routes.const';
import { URL } from '@core/constants/url.const';
import { FormErrorTypes } from '@core/enums/form-error-type.enum';
import { InputMaxLength } from '@core/enums/input-max-length.enum';
import { PhExceptionErrorType } from '@core/enums/ph-exception-error-type.enum';
import { AppInitService } from '@core/services/app-init.service';
import { AppState } from '@core/store';
import { selectIsAuthenticated } from '@core/store/auth';
import { selectEmailCheckErrorType, selectUserPrincipalCheckErrorType } from '@core/store/checkout';
import {
  checkUserByEmail,
  checkUserByUserPrincipal,
  resetEmailCheck,
  resetresetkUserByPrincipalNameCheck,
} from '@core/store/checkout/checkout.actions';
import { selectCurrentConsultant } from '@core/store/consultant';
import { selectRegistrationFailedErrorType, selectUserLoading } from '@core/store/user';
import {
  resetRegistrationFailedErrorType,
  sigunpPhoneUser,
  sigunpPhoneUserFailure,
  sigunpPhoneUserSuccess,
} from '@core/store/user/user.actions';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TextFieldInputComponent } from '@shared/components/text-field-input/text-field-input.component';
import { isMexEnv, isUsaEnv } from '@shared/utils/environment-utils';
import { hideRecaptchaBadge, showRecaptchaBadge } from '@shared/utils/recaptcha-utils';
import { matchValidator } from '@shared/utils/validation.utils';
import { namePatternValidator } from '@shared/validators/name-pattern.validator';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { combineLatest, fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
})
export class RegistrationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('emailInput') emailInput: TextFieldInputComponent;
  @ViewChild('emailConfirmInput') emailConfirmInput: TextFieldInputComponent;
  @ViewChild('phoneNumber') phoneNumberInput: TextFieldInputComponent;

  readonly URL = URL;
  readonly PhErrorTypes = PhExceptionErrorType;
  readonly FormErrorTypes = FormErrorTypes;
  readonly Name = 'name';
  readonly FirstName = 'firstName';
  readonly LastName = 'lastName';
  readonly Email = 'email';
  readonly EmailConfirm = 'emailConfirm';
  readonly PhoneNumber = 'phoneNumber';
  readonly Password = 'password';
  readonly PasswordConfirm = 'passwordConfirm';
  readonly Newsletters = 'newsletters';
  readonly ShareWithConsultant = 'shareWithConsultant';
  readonly contactMethod = 'contactMethod';
  readonly Updates = 'updates';
  readonly SmsTransactionalConsent = 'smsTransactionalConsent';
  readonly SmsPromotionalConsent = 'smsPromotionalConsent';
  readonly showSmsConsent = !isMexEnv;
  readonly ErrorMessages = {
    [this.FormErrorTypes.pattern]: FormErrorMessages.passwordPattern,
    [this.FormErrorTypes.required]: FormErrorMessages.required,
  };

  readonly phoneErrorMessages = {
    ...FormErrorMessages,
    [FormErrorTypes.pattern]: $localize`Please enter a valid phone number`,
  };

  form: FormGroup;
  loading$: Observable<boolean>;
  disableSubmit$: Observable<boolean>;
  errorType$: Observable<PhExceptionErrorType>;
  emailErrorType$: Observable<PhExceptionErrorType>;
  userPrincipalErrorType$: Observable<PhExceptionErrorType>;
  isEmailAddressAdded: boolean = false;
  selectedContactMethod: string = '';
  isPhoneNumberAdded: boolean = false;
  bothContactMethodAdded: boolean = false;
  smsOptOutEmailRequired: boolean = false;
  isAuthenticated$: Observable<boolean>;
  errorMessage: string = '';
  showEmailRequiredMessage: boolean = false;
  showEmailConfirmRequiredMessage: boolean = false;
  showPhoneRequiredMessage: boolean = false;

  readonly LoginViaPhone = AuthRoutes.VerifyPhone;
  readonly LoginEmail = AuthRoutes.Login;
  private subscriptions: Subscription = new Subscription();
  private destroy$ = new Subject<void>();
  private isUsaEnv = isUsaEnv;

  constructor(
    private fb: FormBuilder,
    private store$: Store<AppState>,
    private reCaptchaService: ReCaptchaV3Service,
    private appInitService: AppInitService,
    private cdr: ChangeDetectorRef,
    private actions$: Actions,
    private router: Router,
  ) {}

  onSubmit(): void {
    if (this.form.valid) {
      this.subscriptions.add(
        this.reCaptchaService
          .execute('importantAction')
          .pipe(
            filter((token) => !!token || !this.appInitService.Settings.ec.isRecaptchaEnabled),
            take(1),
            withLatestFrom(this.store$.select(selectCurrentConsultant).pipe(take(1))),
          )
          .subscribe(([token, consultant]) => {
            console.log('Consultant Details in registration:', consultant);
            const emailControl = this.form.get([this.Email, this.Email]);
            const phoneControl = this.form.get(this.PhoneNumber);
            const isUsa = !!this.isUsaEnv;
            const phoneNumber = isUsa ? '+1' + phoneControl?.value : '+52' + phoneControl?.value;
            const email =
              !emailControl?.value || emailControl.value === ''
                ? phoneNumber + '@noemail.com'
                : emailControl.value;

            const userToRegister = {
              firstName: this.form.get([this.Name, this.FirstName])?.value,
              lastName: this.form.get([this.Name, this.LastName])?.value,
              email: email,
              phoneNumber: phoneNumber,
              token: token,
              azureB2CId: '',
              activeConsultantProwessId: consultant?.beeNumber,
              newsletters: this.form.get(this.Newsletters)?.value,
              shareWithConsultant: this.form.get(this.ShareWithConsultant)?.value,
              contactMethod: this.form.get(this.contactMethod)?.value,
              password: this.form.get([this.Password, this.Password])?.value,
              smsTransactionalConsent: this.form.get(this.SmsTransactionalConsent)?.value ?? false,
              smsPromotionalConsent: this.form.get(this.SmsPromotionalConsent)?.value ?? false,
            };

            this.store$.dispatch(sigunpPhoneUser({ payload: userToRegister, token }));
          }),
      );

      this.actions$
        .pipe(ofType(sigunpPhoneUserSuccess), take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.selectedContactMethod === 'phone') {
            this.router.navigateByUrl(this.LoginViaPhone);
          } else {
            const emailControl = this.form.get([this.Email, this.Email]);
            this.router.navigateByUrl(`/login?prefill_email=${emailControl.value}`);
          }
        });

      this.actions$
        .pipe(ofType(sigunpPhoneUserFailure), take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          this.errorMessage = $localize`Account creation failed. Please contact IT`;
        });
    } else {
      this.form.markAllAsTouched();
    }
  }

  ngOnInit(): void {
    this.loading$ = this.store$.select(selectUserLoading);
    this.createUserRegistrationForm();
    this.isAuthenticated$ = this.store$.select(selectIsAuthenticated);
    this.errorType$ = this.store$.select(selectRegistrationFailedErrorType);
    this.emailErrorType$ = this.store$.select(selectEmailCheckErrorType);
    this.userPrincipalErrorType$ = this.store$.select(selectUserPrincipalCheckErrorType);

    this.disableSubmit$ = combineLatest([
      this.store$.select(selectUserLoading),
      this.errorType$,
      this.emailErrorType$,
      this.userPrincipalErrorType$,
      this.form.statusChanges.pipe(startWith(this.form.status)),
      this.form.get(this.contactMethod).valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([loading, error, emailError, userPrincipalError, formStatus, contactMethod]) => {
        if (loading) {
          return true;
        }
        console.log(formStatus);
        if (error === this.PhErrorTypes.ValidationException) {
          return true;
        }

        if (!this.selectedContactMethod && !contactMethod) {
          return true;
        }

        const emailAdded =
          this.selectedContactMethod === 'email' ||
          this.isEmailAddressAdded ||
          this.smsOptOutEmailRequired;
        const phoneAdded = this.selectedContactMethod === 'phone' || this.isPhoneNumberAdded;

        const emailControl = this.form.get([this.Email, this.Email]);
        const emailConfirmControl = this.form.get([this.Email, this.EmailConfirm]);
        const phoneControl = this.form.get(this.PhoneNumber);

        let emailBlocked = false;
        if (emailAdded) {
          const emailValue = emailControl?.value?.trim() || '';
          const emailConfirmValue = emailConfirmControl?.value?.trim() || '';

          const emailEmpty = !emailValue;
          const emailInvalid = emailControl?.invalid;
          const emailHasError = emailError === PhExceptionErrorType.Email;
          const emailConfirmMismatch = emailValue !== emailConfirmValue || !emailConfirmValue;

          emailBlocked = emailEmpty || emailInvalid || emailHasError || emailConfirmMismatch;
        }

        let phoneBlocked = false;
        if (phoneAdded) {
          const phoneValue = phoneControl?.value?.trim() || '';

          const phoneEmpty = !phoneValue;
          const phoneInvalid = phoneControl?.invalid;
          const phoneHasError = userPrincipalError === PhExceptionErrorType.UserPrincipal;

          phoneBlocked = phoneEmpty || phoneInvalid || phoneHasError;
        }

        if (this.bothContactMethodAdded) {
          const result = emailBlocked || phoneBlocked;
          return result;
        }

        const nothingSelected = !emailAdded && !phoneAdded;
        const result = emailBlocked || phoneBlocked || nothingSelected;
        this.cdr.markForCheck();

        return result;
      }),
    );

    this.contactMethodCtrl?.valueChanges.subscribe((v) => {
      this.selectedContactMethod = v;
    });
    const emailCtrl = this.form.get([this.Email, this.Email]);
    const emailConfirmCtrl = this.form.get([this.Email, this.EmailConfirm]);
    const phoneCtrl = this.form.get(this.PhoneNumber);
    const methodCtrl = this.contactMethodCtrl;
    const smsConsentCtrl = this.form.get(this.SmsTransactionalConsent);

    if (emailCtrl) {
      this.subscriptions.add(
        combineLatest([
          this.isAuthenticated$,
          methodCtrl.valueChanges.pipe(startWith(methodCtrl.value)),
          emailCtrl.valueChanges.pipe(startWith(emailCtrl.value)),
          smsConsentCtrl.valueChanges.pipe(startWith(smsConsentCtrl.value)),
        ]).subscribe(([isAuth, method, emailVal, smsConsent]: [boolean, string, any, boolean]) => {
          // phone-only signups that opt out of transactional SMS must provide an email
          const phoneOnly = method === 'phone' && !this.bothContactMethodAdded;
          this.smsOptOutEmailRequired =
            this.showSmsConsent && !isAuth && phoneOnly && !smsConsent;

          if (this.smsOptOutEmailRequired) {
            emailCtrl.setValidators([Validators.required, Validators.email]);
          } else if (phoneOnly) {
            emailCtrl.clearValidators();
          }
          emailCtrl.updateValueAndValidity({ emitEvent: false });

          if (
            !isAuth &&
            !this.bothContactMethodAdded &&
            (method === 'email' || this.smsOptOutEmailRequired)
          ) {
            const emailEmpty = !emailVal || emailVal.toString().trim() === '';
            this.showEmailRequiredMessage = emailEmpty;
          } else {
            this.showEmailRequiredMessage = false;
          }
        }),
      );
    }

    if (emailConfirmCtrl) {
      this.subscriptions.add(
        combineLatest([
          this.isAuthenticated$,
          methodCtrl.valueChanges.pipe(startWith(methodCtrl.value)),
          emailConfirmCtrl.valueChanges.pipe(startWith(emailConfirmCtrl.value)),
          smsConsentCtrl.valueChanges.pipe(startWith(smsConsentCtrl.value)),
        ]).subscribe(
          ([isAuth, method, emailConfirmVal, smsConsent]: [boolean, string, any, boolean]) => {
            const phoneOnly = method === 'phone' && !this.bothContactMethodAdded;
            const requireEmail = this.showSmsConsent && !isAuth && phoneOnly && !smsConsent;

            if (requireEmail) {
              emailConfirmCtrl.setValidators([Validators.required, Validators.email]);
            } else if (phoneOnly) {
              emailConfirmCtrl.clearValidators();
            }
            emailConfirmCtrl.updateValueAndValidity({ emitEvent: false });

            if (!isAuth && !this.bothContactMethodAdded && (method === 'email' || requireEmail)) {
              const confirmEmpty = !emailConfirmVal || emailConfirmVal.toString().trim() === '';
              this.showEmailConfirmRequiredMessage = confirmEmpty;
            } else {
              this.showEmailConfirmRequiredMessage = false;
            }
          },
        ),
      );
    }

    if (phoneCtrl) {
      this.subscriptions.add(
        combineLatest([
          this.isAuthenticated$,
          methodCtrl.valueChanges.pipe(startWith(methodCtrl.value)),
          phoneCtrl.valueChanges.pipe(startWith(phoneCtrl.value)),
        ]).subscribe(([isAuth, method, phoneVal]: [boolean, string, any]) => {
          if (!isAuth && method === 'phone' && !this.bothContactMethodAdded) {
            const phoneEmpty = !phoneVal || phoneVal.toString().trim() === '';
            this.showPhoneRequiredMessage = phoneEmpty;
          } else {
            this.showPhoneRequiredMessage = false;
          }
        }),
      );
    }
  }

  get contactMethodCtrl() {
    return this.form.get(this.contactMethod);
  }

  get showContactInfoRequiredAlert(): boolean {
    const phoneSelected = this.contactMethodCtrl?.value === 'phone' || this.isPhoneNumberAdded;
    const phoneValue = this.form.get(this.PhoneNumber)?.value;
    const hasPhone = !!phoneValue && phoneValue.toString().trim() !== '';
    const emailValue = this.form.get([this.Email, this.Email])?.value;
    const hasEmail = !!emailValue && emailValue.toString().trim() !== '';
    const smsConsent = !!this.form.get(this.SmsTransactionalConsent)?.value;

    return this.showSmsConsent && phoneSelected && hasPhone && !hasEmail && !smsConsent;
  }

  ngAfterViewInit(): void {
    showRecaptchaBadge();
    this.resetRegistrationError();
    this.resetUserPrincipalCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    hideRecaptchaBadge();
    this.store$.dispatch(resetRegistrationFailedErrorType());
  }

  onPhoneBlur() {
    if (this.phoneNumberInput) {
      const phoneKeyup$ = fromEvent(this.phoneNumberInput.input.nativeElement, 'keyup').pipe(
        debounceTime(500),
      );
      const phoneBlur$ = fromEvent(this.phoneNumberInput.input.nativeElement, 'blur');
      this.subscriptions.add(
        merge(phoneKeyup$, phoneBlur$)
          .pipe(
            map(() => this.phoneNumberInput.value),
            distinctUntilChanged(),
            withLatestFrom(this.isAuthenticated$),
            filter(([, isAuth]) => !isAuth),
          )
          .subscribe(([phoneValue]) => {
            let raw = '';
            if (phoneValue != null && phoneValue !== '') {
              raw = phoneValue.toString().replace(/\D/g, '');
            }
            if (!raw) {
              return;
            }
            let normalized: string | null = null;
            if (raw.startsWith('1') && raw.length === 11) {
              normalized = '+' + raw;
            } else if (raw.startsWith('52') && raw.length === 12) {
              normalized = '+' + raw;
            } else if (raw.length === 10) {
              const usa = !!this.isUsaEnv;
              if (usa) {
                normalized = '+1' + raw;
              } else {
                normalized = '+52' + raw;
              }
            }
            if (normalized) {
              this.store$.dispatch(
                checkUserByUserPrincipal({
                  userPrincipal: normalized,
                }),
              );
            }
          }),
      );
    }
  }

  onContactMethodClick(method: string) {
    this.bothContactMethodAdded = false;
    this.selectedContactMethod = method;

    if (method === 'email') {
      this.isEmailAddressAdded = true;
      this.isPhoneNumberAdded = false;
    } else if (method === 'phone') {
      this.isPhoneNumberAdded = true;
      this.isEmailAddressAdded = false;
    }

    this.form.patchValue(
      {
        contactMethod: method,
      },
      { emitEvent: false },
    );

    if (method === 'email') {
      this.emailPhoneValidation('email');

      const phoneControl = this.form.get(this.PhoneNumber);
      if (phoneControl) {
        this.clearControl(phoneControl);
      }

      setTimeout(() => {
        this.validateEmail();
        this.dispatchEmailDuplicateCheck();
      }, 100);
    } else if (method === 'phone') {
      this.emailPhoneValidation('phone');

      const emailControl = this.form.get([this.Email, this.Email]);
      const emailConfirmControl = this.form.get([this.Email, this.EmailConfirm]);
      const emailGroup = this.form.get(this.Email);
      const passwordControl = this.form.get([this.Password, this.Password]);
      const passwordConfirmControl = this.form.get([this.Password, this.PasswordConfirm]);
      const passwordGroup = this.form.get(this.Password);

      if (emailControl) this.clearControl(emailControl);
      if (emailConfirmControl) this.clearControl(emailConfirmControl);
      if (passwordControl) this.clearControl(passwordControl);
      if (passwordConfirmControl) this.clearControl(passwordConfirmControl);

      if (emailGroup) {
        emailGroup.setErrors(null);
        emailGroup.updateValueAndValidity({ emitEvent: false });
      }

      if (passwordGroup) {
        passwordGroup.setErrors(null);
        passwordGroup.updateValueAndValidity({ emitEvent: false });
      }

      this.store$.dispatch(resetEmailCheck());

      setTimeout(() => {
        this.onPhoneBlur();
        this.dispatchPhoneDuplicateCheck();
      }, 100);
    }
  }

  addContact() {
    this.bothContactMethodAdded = true;
    this.isEmailAddressAdded = true;
    this.isPhoneNumberAdded = true;

    this.applyBothContactValidation();

    this.subscriptions.add(
      this.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
        setTimeout(() => {
          if (!isAuthenticated) {
            this.validateEmail();
            this.dispatchEmailDuplicateCheck();
          }
          this.onPhoneBlur();
          this.dispatchPhoneDuplicateCheck();
        }, 100);
      }),
    );

    this.form.get('phoneNumber')?.markAsTouched();
    this.form.get(`${this.Email}.${this.Email}`);
    if (this.form.get(this.EmailConfirm)) {
      this.form.get(this.EmailConfirm)?.markAsTouched();
    }
  }

  validateEmail() {
    if (this.emailInput && this.emailConfirmInput) {
      const emailBlur$ = fromEvent(this.emailInput.input.nativeElement, 'blur').pipe(startWith({}));
      const emailKeyup$ = fromEvent(this.emailInput.input.nativeElement, 'keyup').pipe(
        debounceTime(500),
        startWith({}),
        distinctUntilChanged(),
      );
      const emailConfirmBlur$ = fromEvent(this.emailConfirmInput.input.nativeElement, 'blur').pipe(
        startWith({}),
      );
      const emailConfirmKeyup$ = fromEvent(
        this.emailConfirmInput.input.nativeElement,
        'keyup',
      ).pipe(debounceTime(500), startWith({}), distinctUntilChanged());

      this.subscriptions.add(
        combineLatest([emailBlur$, emailKeyup$, emailConfirmBlur$, emailConfirmKeyup$])
          .pipe(withLatestFrom(this.isAuthenticated$.pipe(filter((isAuth) => !isAuth))))
          .subscribe(() => {
            const emailField = this.form.get([this.Email, this.Email]);

            this.updateEmailConfirmValidity();

            if (
              emailField?.valid &&
              this.emailInput.value === this.emailConfirmInput.value &&
              this.emailInput.value !== ''
            ) {
              this.store$.dispatch(checkUserByEmail({ email: this.emailInput.value }));
            }
          }),
      );
    }
  }

  private updateEmailConfirmValidity(): void {
    const emailControl = this.form.get([this.Email, this.Email]);
    const emailConfirmControl = this.form.get([this.Email, this.EmailConfirm]);

    if (!emailControl || !emailConfirmControl) {
      return;
    }

    const emailValue = emailControl.value?.trim() || '';
    const emailConfirmValue = emailConfirmControl.value?.trim() || '';

    const currentGroupErrors = this.form.errors || {};

    if (emailValue !== emailConfirmValue && emailConfirmValue !== '') {
      this.form.setErrors({
        ...currentGroupErrors,
        [this.FormErrorTypes.emailConfirm]: true,
      });
    } else {
      if (currentGroupErrors[this.FormErrorTypes.emailConfirm]) {
        delete currentGroupErrors[this.FormErrorTypes.emailConfirm];
        const remainingErrors =
          Object.keys(currentGroupErrors).length > 0 ? currentGroupErrors : null;
        this.form.setErrors(remainingErrors);
      }
    }
  }

  private applyBothContactValidation() {
    const emailControl = this.form.get([this.Email, this.Email]);
    const phoneControl = this.form.get('phoneNumber');
    const emailConfirmControl = this.form.get([this.Email, this.EmailConfirm]);
    phoneControl?.setValidators([
      Validators.required,
      Validators.pattern(/^(\+?(?:1|52)\d{10}|\d{10})$/),
    ]);
    emailControl?.setValidators([Validators.required, Validators.email]);

    if (emailConfirmControl) {
      emailConfirmControl?.setValidators([Validators.required, Validators.email]);
    }

    emailControl?.updateValueAndValidity({ emitEvent: false });
    phoneControl?.updateValueAndValidity({ emitEvent: false });
    emailConfirmControl?.updateValueAndValidity({ emitEvent: false });
  }

  private clearControl(ctrl: AbstractControl) {
    ctrl.reset('', { emitEvent: false });

    ctrl.markAsPristine();
    ctrl.markAsUntouched();
    ctrl.setErrors(null);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private emailPhoneValidation(validationPref: string) {
    const emailControl = this.form?.get([this.Email, this.Email]);
    const phoneControl = this.form.get('phoneNumber');
    const emailConfirmControl = this.form.get([this.Email, this.EmailConfirm]);
    const passwordControl = this.form.get([this.Password, this.Password]);
    const passwordConfirmControl = this.form.get([this.Password, this.PasswordConfirm]);
    const passwordGroup = this.form.get(this.Password);

    if (validationPref === 'phone') {
      emailControl?.clearValidators();
      emailConfirmControl?.clearValidators();
      passwordControl?.clearValidators();
      passwordConfirmControl?.clearValidators();

      passwordGroup?.clearValidators();

      phoneControl?.setValidators([
        Validators.required,
        Validators.pattern(/^(\+?(?:1|52)\d{10}|\d{10})$/),
      ]);
    } else if (validationPref === 'email') {
      phoneControl?.clearValidators();

      emailControl?.setValidators([Validators.required, Validators.email]);
      emailConfirmControl?.setValidators([Validators.required, Validators.email]);

      passwordControl?.setValidators([Validators.required, Validators.pattern(PasswordPattern)]);
      passwordConfirmControl?.setValidators([Validators.required]);

      passwordGroup?.setValidators(
        matchValidator(this.Password, this.PasswordConfirm, this.FormErrorTypes.passwordConfirm),
      );
    }

    emailControl?.updateValueAndValidity({ emitEvent: false });
    phoneControl?.updateValueAndValidity({ emitEvent: false });
    emailConfirmControl?.updateValueAndValidity({ emitEvent: false });
    passwordControl?.updateValueAndValidity({ emitEvent: false });
    passwordConfirmControl?.updateValueAndValidity({ emitEvent: false });

    this.form.get(this.Email)?.updateValueAndValidity({ emitEvent: false });
    passwordGroup?.updateValueAndValidity({ emitEvent: false });
  }

  private resetEmailCheck(): void {
    const emailField = this.form.get([this.Email, this.Email]);
    const emailConfirmField = this.form.get([this.Email, this.EmailConfirm]);
    this.subscriptions.add(
      combineLatest([emailField.valueChanges, emailConfirmField.valueChanges]).subscribe(() =>
        this.store$.dispatch(resetEmailCheck()),
      ),
    );
  }

  private resetUserPrincipalCheck(): void {
    const phoneNumberField = this.form.get(`${this.PhoneNumber}`);

    this.subscriptions.add(
      combineLatest([phoneNumberField.valueChanges]).subscribe(() =>
        this.store$.dispatch(resetresetkUserByPrincipalNameCheck()),
      ),
    );
  }

  private createUserRegistrationForm(): void {
    this.form = this.fb.group({
      [this.Name]: this.fb.group({
        [this.FirstName]: [
          '',
          [Validators.required, Validators.maxLength(InputMaxLength.Fifty), namePatternValidator()],
        ],
        [this.LastName]: [
          '',
          [Validators.required, Validators.maxLength(InputMaxLength.Fifty), namePatternValidator()],
        ],
      }),
      [this.Email]: this.fb.group({
        [this.Email]: [''],
        [this.EmailConfirm]: [''],
      }),
      [this.Password]: this.fb.group(
        {
          [this.Password]: ['', [Validators.required, Validators.pattern(PasswordPattern)]],
          [this.PasswordConfirm]: ['', [Validators.required]],
        },
        {
          validators: matchValidator(
            this.Password,
            this.PasswordConfirm,
            this.FormErrorTypes.passwordConfirm,
          ),
        },
      ),
      [this.PhoneNumber]: [''],
      [this.ShareWithConsultant]: [true],
      [this.contactMethod]: ['', [Validators.required]],
      [this.Updates]: [false],
      [this.SmsTransactionalConsent]: [false],
      [this.SmsPromotionalConsent]: [false],
    });
  }

  private resetRegistrationError(): void {
    this.subscriptions.add(
      combineLatest([
        this.form.get(this.Email).get(this.Email).valueChanges,
        this.form.get(this.Email).get(this.EmailConfirm).valueChanges,
      ]).subscribe(() => this.store$.dispatch(resetRegistrationFailedErrorType())),
    );
  }

  private dispatchEmailDuplicateCheck(): void {
    const emailControl = this.form.get([this.Email, this.Email]);
    const emailConfirmControl = this.form.get([this.Email, this.EmailConfirm]);
    if (!emailControl || !emailConfirmControl) {
      return;
    }
    const emailVal = emailControl.value?.trim() || '';
    const emailConfirmVal = emailConfirmControl.value?.trim() || '';
    if (
      emailControl.valid &&
      emailVal !== '' &&
      emailConfirmVal !== '' &&
      emailVal === emailConfirmVal
    ) {
      this.store$.dispatch(checkUserByEmail({ email: emailVal }));
    }
  }

  private normalizePhoneForCheck(phoneValue: any): string | null {
    if (phoneValue == null || phoneValue === '') {
      return null;
    }
    const raw = phoneValue.toString().replace(/\D/g, '');
    let normalized: string | null = null;
    if (raw.startsWith('1') && raw.length === 11) {
      normalized = '+' + raw;
    } else if (raw.startsWith('52') && raw.length === 12) {
      normalized = '+' + raw;
    } else if (raw.length === 10) {
      const usa = !!this.isUsaEnv;
      normalized = (usa ? '+1' : '+52') + raw;
    }
    return normalized;
  }

  private dispatchPhoneDuplicateCheck(): void {
    const phoneControl = this.form.get(this.PhoneNumber);
    const phoneVal = phoneControl?.value;
    const normalized = this.normalizePhoneForCheck(phoneVal);
    if (normalized) {
      this.store$.dispatch(checkUserByUserPrincipal({ userPrincipal: normalized }));
    }
  }
}
